import dayjs from "dayjs";

import * as T from "@src/types";
import type { ApiCandle } from "@tradingdroid/frontends-types";

export function ensureSecondsTs(timestamp: number) {
  return timestamp > 10000000000 ? Math.floor(timestamp / 1000) : timestamp;
}

/**
 * Default chart options for TradingView Light charts
 * Matches the styling used in Streamlit implementation
 */
export const defaultTVLightOptions: T.TVL.DeepPartial<T.TVL.ChartOptions> = {
  rightPriceScale: {
    visible: true,
  },
  leftPriceScale: {
    visible: true,
    invertScale: true,
    mode: T.TVL.PriceScaleMode.Normal,
    autoScale: true,
  },
  layout: {
    background: {
      type: T.TVL.ColorType.Solid,
      color: "#0f0f0f",
    },
    textColor: "#d1d4dc",
  },
  crosshair: {
    mode: T.TVL.CrosshairMode.Normal,
  },
  grid: {
    vertLines: {
      color: "rgba(42, 46, 57, 0.6)",
    },
    horzLines: {
      color: "rgba(42, 46, 57, 0.6)",
    },
  },
};

/**
 * Prepare props for TradingView Light chart with optional custom options
 * @param series - Chart series data
 * @param customOptions - Optional custom chart options to override defaults
 * @param height - Optional chart height (default: 250)
 */
export const preparePropsForLightChart = (
  series: T.RunResult["series"],
  customOptions?: T.TVL.DeepPartial<T.TVL.ChartOptions>,
  height = 250,
): T.TVLightProps => {
  return {
    series,
    height,
    options: {
      ...defaultTVLightOptions,
      ...customOptions,
    },
  };
};

export const candlesToTvProps = (
  candles: ApiCandle[],
  title: string,
  height = 200,
): T.TVLightProps => {
  const sorted = [...candles].sort((a, b) => dayjs(a.open_time).unix() - dayjs(b.open_time).unix());
  const deduped = Array.from(new Map(sorted.map((c) => [dayjs(c.open_time).unix(), c])).values());

  const data = deduped.map((c) => ({
    time: dayjs(c.open_time).unix() as T.TVL.Time,
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
  }));

  const series: T.TVLightSeries[] = [
    {
      data,
      type: "Candlestick",
      options: {
        title,
      },
    },
  ];

  const tvProps = preparePropsForLightChart(series, {}, height);
  return tvProps;
};
